<template>
	<tr
		ref="rowRef"
		class="table-row"
		:class="{
			active: isActive,
			'menu-open': menuOpen || translationsPopoverOpen,
			'table-row--with-assignment-columns': showAssignmentColumns,
		}"
		tabindex="0"
		aria-haspopup="menu"
		:aria-expanded="menuOpen"
		:aria-label="actionsMenuLabel"
		@click="handleRowClick"
		@keydown.enter.self.prevent="toggleMenu(true)"
		@keydown.space.self.prevent="toggleMenu(true)"
		@keydown.esc.stop="closePopovers"
	>
		<td class="col-title">
			<div class="title-cell-content">
				<input
					v-if="isRenaming"
					ref="titleInputRef"
					v-model="formModel.title"
					type="text"
					class="cell-input title-input"
					placeholder="Document title"
					:disabled="isBusy || hasActiveSigning"
					:title="hasActiveSigning ? 'Finish the active signing flow before renaming this document' : ''"
					@click.stop
					@input="markTitleChanged"
					@blur="finishRename"
					@keydown.enter.stop.prevent="$event.currentTarget.blur()"
					@keydown.esc.stop.prevent="cancelRename"
				/>
				<span
					v-else
					class="title-text"
					v-tooltip="() => formModel.title || 'Untitled document'"
				>
					{{ formModel.title || 'Untitled document' }}
				</span>
				<span v-if="autosaveLabel" class="autosave-hint" :class="autosaveClass">
					{{ autosaveLabel }}
				</span>
			</div>
		</td>

		<td class="col-mode">
			<div class="badge-cell" ref="completionPopRef">
				<button
					type="button"
					class="status-badge"
					:class="completionBadgeClass"
					:disabled="isBusy"
					@click.stop="toggleCompletionPopover"
				>
					{{ completionBadgeLabel }}
				</button>
				<transition name="status-popover">
					<div
						v-if="completionPopoverOpen"
						class="status-popover status-popover--completion"
						@click.stop
					>
						<p class="popover-title">Completion mode</p>
						<div class="popover-options">
							<button
								v-for="option in completionModeOptions"
								:key="option.value"
								type="button"
								class="popover-option"
								:class="{ selected: formModel.completionMode === option.value }"
								@pointerdown.prevent="selectCompletionMode(option.value)"
								@click="selectCompletionMode(option.value)"
							>
								<span class="status-badge" :class="badgeClassForMode(option.value)">
									{{ option.label }}
								</span>
								<span class="popover-option-help">{{ option.help }}</span>
							</button>
						</div>
					</div>
				</transition>
			</div>

			<div
				v-if="menuOpen"
				ref="menuRef"
				class="row-submenu"
				:style="menuStyle"
				role="menu"
				@pointerdown.stop
				@click.stop
				@keydown.esc.prevent.stop="handleMenuEscape"
				@keydown.down.prevent.stop="focusAdjacentMenuItem(1)"
				@keydown.up.prevent.stop="focusAdjacentMenuItem(-1)"
				@keydown.home.prevent.stop="focusBoundaryMenuItem('first')"
				@keydown.end.prevent.stop="focusBoundaryMenuItem('last')"
			>
					<button
						type="button"
						class="submenu-btn"
						role="menuitem"
						:disabled="hasActiveSigning"
						@click="startRename"
					>
						Rename document
					</button>
					<button
						v-if="isTemplateDocument"
						type="button"
						class="submenu-btn"
						role="menuitem"
						@click="handleOpenDocument"
					>
						Edit document
					</button>
					<button
						type="button"
						class="submenu-btn"
						role="menuitem"
						:disabled="!canPreview || isBusy"
						@click="handlePreview"
					>
						Preview document
					</button>
					<button
						type="button"
						class="submenu-btn submenu-btn--with-meta"
						role="menuitem"
						aria-haspopup="dialog"
						:title="translationsSummary"
						:disabled="!canManageTranslations || isBusy"
						@click="toggleTranslationsPopover"
					>
						<span>Translations</span>
						<span class="submenu-meta">{{ translationsReadySummary }}</span>
					</button>
					<div
						ref="categoryAnchorRef"
						class="category-flyout-anchor"
						@mouseenter="openCategoryMenu"
						@mouseleave="handleCategoryMouseLeave"
						@focusout="handleCategoryFocusOut"
					>
						<button
							type="button"
							class="submenu-btn submenu-btn--with-meta"
							role="menuitem"
							aria-haspopup="menu"
							:aria-controls="categoryMenuId"
							:aria-expanded="categoryMenuOpen"
							@focus="handleCategoryTriggerFocus"
							@click.stop="openCategoryMenu"
							@keydown.right.prevent.stop="focusCategoryMenuItem('first')"
							@keydown.down.prevent.stop="focusCategoryMenuItem('first')"
						>
							<span>Move to category</span>
							<span aria-hidden="true">›</span>
						</button>
						<div
							v-if="categoryMenuOpen"
							:id="categoryMenuId"
							ref="categoryMenuRef"
							class="category-submenu"
							:class="`category-submenu--${categoryMenuPlacement}`"
							:style="categoryMenuStyle"
							role="menu"
							aria-label="Move to category"
							@keydown.left.prevent.stop="closeCategoryMenuAndFocusTrigger"
							@keydown.esc.prevent.stop="closeCategoryMenuAndFocusTrigger"
							@keydown.down.prevent.stop="focusAdjacentCategoryMenuItem(1)"
							@keydown.up.prevent.stop="focusAdjacentCategoryMenuItem(-1)"
							@keydown.home.prevent.stop="focusCategoryMenuItem('first')"
							@keydown.end.prevent.stop="focusCategoryMenuItem('last')"
						>
							<button
								v-for="category in documentCategoryOptions"
								:key="category.value"
								type="button"
								class="category-submenu-option"
								role="menuitemradio"
								:aria-checked="currentCategory === category.value"
								@click="handleMoveCategory(category.value)"
							>
								<span>{{ category.label }}</span>
								<span v-if="currentCategory === category.value" aria-hidden="true">✓</span>
							</button>
						</div>
					</div>
					<div class="submenu-divider" role="separator"></div>
					<button
						type="button"
						class="submenu-btn delete"
						role="menuitem"
						@click="handleArchive"
					>
						Archive document
					</button>
			</div>

			<Teleport to="body">
			<div
				v-if="translationsPopoverOpen"
				class="translation-modal-overlay"
				@pointerdown.stop
				@click.self.stop="closeTranslationsModal"
			>
					<section
						ref="translationModalRef"
						class="translation-modal"
						role="dialog"
						aria-modal="true"
						:aria-labelledby="translationsModalTitleId"
						tabindex="-1"
						@click.stop
						@keydown.esc.prevent.stop="closeTranslationsModal"
						@keydown.tab="trapTranslationModalFocus"
					>
						<header class="translation-modal-header">
							<div>
								<h3 :id="translationsModalTitleId" class="translation-modal-title">Translations</h3>
								<p class="translation-modal-subtitle">
									{{ props.document?.title || 'Supplier document' }}
								</p>
							</div>
							<button type="button" class="translation-modal-close" @click="closeTranslationsModal">
								Close
							</button>
						</header>
						<div class="translation-list">
							<div
								v-for="translation in translationItems"
								:key="translation.language"
								class="translation-item"
							>
								<span class="translation-lang">{{ translation.shortLabel }}</span>
								<span class="translation-status" :class="translationStatusClass(translation)">
									{{ translationStatusLabel(translation.status) }}
								</span>
								<div class="translation-actions">
									<button
										type="button"
										class="link-action translation-action"
										:disabled="!canPreviewTranslation(translation) || isBusy"
										@click="handleTranslationPreview(translation)"
									>
										Preview
									</button>
									<button
										type="button"
										class="link-action translation-action"
										:disabled="isBusy || translation.status === 'generating'"
										@click="handleTranslationRegenerate(translation)"
									>
										Regenerate
									</button>
								</div>
							</div>
						</div>
					</section>
			</div>
			</Teleport>
		</td>

		<td v-if="showAssignmentColumns" class="col-reference">
			<span class="mobile-cell-label">Reference</span>
			<span class="assignment-cell-value" v-tooltip="() => assignmentReference">
				{{ assignmentReference }}
			</span>
		</td>

		<td v-if="showAssignmentColumns" class="col-counterparty">
			<span class="mobile-cell-label">Counterparty</span>
			<span class="assignment-cell-value" v-tooltip="() => assignmentCounterparty">
				{{ assignmentCounterparty }}
			</span>
		</td>

		<td v-if="showAssignmentColumns" class="col-site-address">
			<span class="mobile-cell-label">Site address</span>
			<span class="assignment-cell-value" v-tooltip="() => assignmentSiteAddress">
				{{ assignmentSiteAddress }}
			</span>
		</td>

		<td v-if="showAssignmentColumns" class="col-amount numeric">
			<span class="mobile-cell-label">Amount</span>
			<span class="assignment-cell-value" v-tooltip="() => assignmentAmount">
				{{ assignmentAmount }}
			</span>
		</td>
	</tr>
</template>

<script setup>
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	watch,
} from 'vue';
import {
	DOCUMENT_TRANSLATION_LANGUAGES,
	SUPPLIER_DOCUMENT_CATEGORIES,
	normalizeSupplierDocumentCategory,
	normalizeSupplierDocumentSourceType,
} from '@/stores/supplierPortalAdmin';
import {
	getAssignmentConfirmationTableSummary,
	isAssignmentConfirmationDocument,
} from '@/utils/assignmentConfirmationDocument';
import { formatGbpAmount } from '@/utils/proposalDocument';
import vTooltip from '@/directives/tooltip';

const AUTOSAVE_DELAY = 600;

const COMPLETION_MODE_OPTIONS = [
	{
		value: 'read_only',
		label: 'Read only',
		help: 'View & download.',
	},
	{
		value: 'bilateral_signature',
		label: 'Bilateral signature',
		help: 'Owner signs first with OTP, then supplier signs.',
	},
];

const props = defineProps({
	document: { type: Object, required: true },
	isActive: { type: Boolean, default: false },
	autoFocus: { type: Boolean, default: false },
	showAssignmentColumns: { type: Boolean, default: false },
});

const emit = defineEmits([
	'archive',
	'open-document',
	'request-preview-url',
	'translation-preview',
	'translation-regenerate',
	'move-category',
	'autosave-error',
	'menu-open',
	'completion-mode-change-request',
]);

const formModel = reactive({
	title: '',
	description: '',
	completionMode: 'read_only',
	requiresEffectiveDate: false,
});

const titleInputRef = ref(null);
const completionPopRef = ref(null);
const rowRef = ref(null);
const menuRef = ref(null);
const categoryAnchorRef = ref(null);
const categoryMenuRef = ref(null);
const translationModalRef = ref(null);
const menuAnchor = reactive({ top: 12, left: 12 });
const menuPosition = reactive({ top: 12, left: 12 });
const categoryMenuPosition = reactive({ top: 12, left: 12 });
const categoryMenuPlacement = ref('right');
const completionPopoverOpen = ref(false);
const translationsPopoverOpen = ref(false);
const menuOpen = ref(false);
const categoryMenuOpen = ref(false);
const suppressCategoryFocusOpen = ref(false);
const isRenaming = ref(false);
const renameOriginalTitle = ref('');
const renameStartVersion = ref(0);
const autosaveState = ref('idle');
const autosaveTimer = ref(null);
const formVersion = ref(0);
const savedVersion = ref(0);
const isSyncingForm = ref(false);
const savePromise = ref(null);
const translationModalEnvironment = {
	bodyOverflow: null,
	appRoot: null,
	appRootWasInert: false,
};

const completionModeOptions = COMPLETION_MODE_OPTIONS;
const documentCategoryOptions = SUPPLIER_DOCUMENT_CATEGORIES;

const isBusy = computed(() => Boolean(props.document?.__saving));

const hasActiveSigning = computed(() => {
	const summary = props.document?.signingSummary || {};
	return (
		(Number(summary.pendingExecutions) || 0) > 0 ||
		(Number(summary.awaitingSupplierExecutions) || 0) > 0
	);
});

const isDirty = computed(() => formVersion.value !== savedVersion.value);

const hasOriginalFile = computed(() => Boolean(props.document?.file?.id));

const hasTemplatePreviewContent = computed(
	() =>
		isAssignmentConfirmationDocument(props.document) &&
		Boolean(
			props.document?.content &&
				typeof props.document.content === 'object' &&
				!Array.isArray(props.document.content) &&
				Object.keys(props.document.content).length,
		),
);

const canPreview = computed(() => hasOriginalFile.value || hasTemplatePreviewContent.value);

const canManageTranslations = computed(() => Boolean(props.document?.file?.id));

const currentCategory = computed(() =>
	normalizeSupplierDocumentCategory(props.document?.category),
);

const isTemplateDocument = computed(
	() => normalizeSupplierDocumentSourceType(props.document?.sourceType) === 'template',
);

const assignmentSummary = computed(() => getAssignmentConfirmationTableSummary(props.document));
const assignmentReference = computed(() => assignmentSummary.value?.reference || '—');
const assignmentCounterparty = computed(() => assignmentSummary.value?.counterparty || '—');
const assignmentSiteAddress = computed(() => assignmentSummary.value?.siteAddress || '—');
const assignmentAmount = computed(() =>
	assignmentSummary.value?.amount === null || assignmentSummary.value?.amount === undefined
		? '—'
		: formatGbpAmount(assignmentSummary.value.amount),
);
const actionsMenuLabel = computed(
	() => `Open actions for ${props.document?.title || 'supplier document'}`,
);

const menuStyle = computed(() => ({
	top: `${menuPosition.top}px`,
	left: `${menuPosition.left}px`,
}));

const documentDomId = computed(() =>
	String(props.document?.id ?? 'document').replace(/[^a-zA-Z0-9_-]/g, '-'),
);
const categoryMenuId = computed(() => `document-category-menu-${documentDomId.value}`);
const translationsModalTitleId = computed(
	() => `document-translations-title-${documentDomId.value}`,
);
const categoryMenuStyle = computed(() => ({
	top: `${categoryMenuPosition.top}px`,
	left: `${categoryMenuPosition.left}px`,
}));

const translationItems = computed(() => {
	const byLanguage = new Map();
	(Array.isArray(props.document?.translations) ? props.document.translations : []).forEach(
		(translation) => {
			if (!translation?.language) return;
			byLanguage.set(String(translation.language), translation);
		},
	);
	return DOCUMENT_TRANSLATION_LANGUAGES.map((language) => ({
		...language,
		id: null,
		status: 'pending',
		file: null,
		...(byLanguage.get(language.language) || {}),
	}));
});

const translationStatusLabel = (status) => {
	const value = String(status || 'pending');
	if (value === 'ready') return 'Ready';
	if (value === 'generating') return 'Generating';
	if (value === 'failed') return 'Failed';
	if (value === 'stale') return 'Stale';
	return 'Pending';
};

const translationStatusClass = (translation) => ({
	'translation-ready': translation?.status === 'ready',
	'translation-generating': translation?.status === 'generating',
	'translation-failed': translation?.status === 'failed',
	'translation-stale': translation?.status === 'stale',
	'translation-pending': !translation?.status || translation?.status === 'pending',
});

const translationsSummary = computed(() => {
	const total = translationItems.value.length;
	const ready = translationItems.value.filter((t) => t?.status === 'ready').length;
	const failed = translationItems.value.filter((t) => t?.status === 'failed').length;
	if (failed) return `${failed} translation${failed === 1 ? '' : 's'} failed — click to manage`;
	if (ready < total) return `${ready}/${total} translations ready — click to manage`;
	return `All ${total} translations ready — click to manage`;
});

const translationsReadySummary = computed(() => {
	const total = translationItems.value.length;
	const ready = translationItems.value.filter(
		(translation) => translation?.status === 'ready',
	).length;
	return `${ready}/${total}`;
});

const canPreviewTranslation = (translation) =>
	translation?.status === 'ready' && Boolean(translation?.file?.id);

const completionBadgeLabel = computed(() => {
	const opt = COMPLETION_MODE_OPTIONS.find(
		(item) => item.value === formModel.completionMode,
	);
	return opt?.label || 'Read only';
});

const completionBadgeClass = computed(() =>
	badgeClassForMode(formModel.completionMode),
);

function badgeClassForMode(mode) {
	if (mode === 'bilateral_signature') return 'status-bilateral';
	return 'status-readonly';
}

const autosaveLabel = computed(() => {
	if (autosaveState.value === 'saving') return 'Saving…';
	if (autosaveState.value === 'error') return 'Save failed';
	if (isDirty.value) return 'Unsaved';
	return '';
});

const autosaveClass = computed(() => ({
	'autosave-saving': autosaveState.value === 'saving',
	'autosave-error': autosaveState.value === 'error',
	'autosave-dirty': isDirty.value && autosaveState.value !== 'saving',
}));

const clearAutosaveTimer = () => {
	if (autosaveTimer.value) {
		clearTimeout(autosaveTimer.value);
		autosaveTimer.value = null;
	}
};

const syncFromDocument = (doc) => {
	isSyncingForm.value = true;
	formModel.title = doc?.title || '';
	formModel.description = doc?.description || '';
	formModel.completionMode =
		doc?.completionMode === 'bilateral_signature' ? 'bilateral_signature' : 'read_only';
	formModel.requiresEffectiveDate =
		formModel.completionMode === 'bilateral_signature';
	formVersion.value = 0;
	savedVersion.value = 0;
	autosaveState.value = 'idle';
	nextTick(() => {
		isSyncingForm.value = false;
	});
};

watch(
	() => props.document,
	(doc) => {
		if (!doc) return;
		if (isDirty.value || autosaveState.value === 'saving') return;
		syncFromDocument(doc);
	},
	{ immediate: true, deep: true },
);

const markChanged = () => {
	if (isSyncingForm.value) return;
	formVersion.value += 1;
	scheduleAutosave();
};

const markTitleChanged = () => {
	if (isSyncingForm.value) return;
	formVersion.value += 1;
	clearAutosaveTimer();
	if (autosaveState.value === 'error') {
		autosaveState.value = 'idle';
	}
};

const flushIfDirty = () => {
	if (!isDirty.value) return;
	clearAutosaveTimer();
	void runAutosave();
};

const scheduleAutosave = () => {
	if (!isDirty.value) return;
	clearAutosaveTimer();
	if (autosaveState.value === 'error') {
		autosaveState.value = 'idle';
	}
	autosaveTimer.value = setTimeout(() => {
		autosaveTimer.value = null;
		void runAutosave();
	}, AUTOSAVE_DELAY);
};

const buildPayload = () => {
	const title = String(formModel.title || '').trim();
	const completionMode =
		formModel.completionMode === 'bilateral_signature'
			? 'bilateral_signature'
			: 'read_only';
	return {
		title,
		description: formModel.description || '',
		completionMode,
		requiresEffectiveDate: completionMode === 'bilateral_signature',
		file: null,
	};
};

const runAutosave = async () => {
	if (!isDirty.value || autosaveState.value === 'saving') return savePromise.value;
	if (!props.document?.id) return null;
	const title = String(formModel.title || '').trim();
	if (!title) {
		autosaveState.value = 'idle';
		return null;
	}
	const versionAtRequest = formVersion.value;
	autosaveState.value = 'saving';
	const task = (async () => {
		try {
			const payload = buildPayload();
			await emitSaveRequest(payload);
			savedVersion.value = versionAtRequest;
			autosaveState.value = 'idle';
			if (formVersion.value !== versionAtRequest) {
				scheduleAutosave();
			}
		} catch (err) {
			autosaveState.value = 'error';
			emit('autosave-error', err);
			throw err;
		}
	})();

	savePromise.value = task;
	try {
		return await task;
	} finally {
		savePromise.value = null;
	}
};

let saveHandler = null;

const setSaveHandler = (handler) => {
	saveHandler = handler;
};

const emitSaveRequest = async (payload) => {
	if (typeof saveHandler === 'function') {
		await saveHandler(payload);
	}
};

const handlePreview = () => {
	if (!canPreview.value) return;
	closeMenu();
	emit('request-preview-url', { document: props.document });
};

const handleTranslationPreview = (translation) => {
	if (!canPreviewTranslation(translation)) return;
	closeTranslationsPopover();
	emit('translation-preview', {
		document: props.document,
		language: translation.language,
	});
};

const handleTranslationRegenerate = (translation) => {
	if (!canManageTranslations.value || !translation?.language || translation.status === 'generating') return;
	emit('translation-regenerate', {
		document: props.document,
		language: translation.language,
	});
};

const toggleCompletionPopover = () => {
	if (completionPopoverOpen.value) {
		completionPopoverOpen.value = false;
		return;
	}
	closeTranslationsPopover();
	closeMenu();
	completionPopoverOpen.value = true;
	emit('menu-open', { id: props.document?.id });
};

const closeCompletionPopover = () => {
	completionPopoverOpen.value = false;
};

const lockTranslationModalEnvironment = () => {
	if (typeof document === 'undefined' || translationModalEnvironment.bodyOverflow !== null) return;
	translationModalEnvironment.bodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	const appRoot = document.getElementById('app');
	if (appRoot && !appRoot.contains(translationModalRef.value)) {
		translationModalEnvironment.appRoot = appRoot;
		translationModalEnvironment.appRootWasInert = Boolean(appRoot.inert);
		appRoot.inert = true;
	}
};

const unlockTranslationModalEnvironment = () => {
	if (typeof document === 'undefined' || translationModalEnvironment.bodyOverflow === null) return;
	document.body.style.overflow = translationModalEnvironment.bodyOverflow;
	translationModalEnvironment.bodyOverflow = null;
	if (translationModalEnvironment.appRoot) {
		translationModalEnvironment.appRoot.inert = translationModalEnvironment.appRootWasInert;
		translationModalEnvironment.appRoot = null;
		translationModalEnvironment.appRootWasInert = false;
	}
};

const toggleTranslationsPopover = () => {
	if (!canManageTranslations.value) return;
	if (translationsPopoverOpen.value) {
		closeTranslationsModal();
		return;
	}
	closeCompletionPopover();
	closeMenu();
	translationsPopoverOpen.value = true;
	emit('menu-open', { id: props.document?.id });
	nextTick(() => {
		if (!translationsPopoverOpen.value) return;
		lockTranslationModalEnvironment();
		translationModalRef.value?.focus?.();
	});
};

const closeTranslationsPopover = () => {
	translationsPopoverOpen.value = false;
	unlockTranslationModalEnvironment();
};

const closeTranslationsModal = () => {
	closeTranslationsPopover();
	nextTick(() => rowRef.value?.focus?.());
};

const trapTranslationModalFocus = (event) => {
	const focusable = Array.from(
		translationModalRef.value?.querySelectorAll(
			'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
		) || [],
	);
	if (!focusable.length) {
		event.preventDefault();
		translationModalRef.value?.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (document.activeElement === translationModalRef.value) {
		event.preventDefault();
		(event.shiftKey ? last : first).focus?.();
	} else if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

const calculateCategoryMenuPosition = () => {
	const parentMenuRect = menuRef.value?.getBoundingClientRect?.();
	const anchorRect = categoryAnchorRef.value?.getBoundingClientRect?.();
	const categoryRect = categoryMenuRef.value?.getBoundingClientRect?.();
	if (!parentMenuRect || !anchorRect) return;

	const submenuWidth = categoryRect?.width || categoryMenuRef.value?.offsetWidth || 190;
	const submenuHeight =
		categoryRect?.height ||
		categoryMenuRef.value?.offsetHeight ||
		documentCategoryOptions.length * 38 + 12;
	const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : parentMenuRect.right + submenuWidth;
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : anchorRect.top + submenuHeight;
	const padding = 12;
	const gap = 6;

	let left = parentMenuRect.right + gap;
	categoryMenuPlacement.value = 'right';
	if (left + submenuWidth > viewportWidth - padding) {
		left = parentMenuRect.left - submenuWidth - gap;
		categoryMenuPlacement.value = 'left';
	}
	let top = anchorRect.top;
	if (top + submenuHeight > viewportHeight - padding) {
		top = viewportHeight - submenuHeight - padding;
	}

	categoryMenuPosition.left = Math.max(padding, left);
	categoryMenuPosition.top = Math.max(padding, top);
};

const openCategoryMenu = () => {
	if (!menuOpen.value) return;
	categoryMenuOpen.value = true;
	nextTick(() => calculateCategoryMenuPosition());
};

const handleCategoryTriggerFocus = () => {
	if (suppressCategoryFocusOpen.value) return;
	openCategoryMenu();
};

const closeCategoryMenu = () => {
	categoryMenuOpen.value = false;
};

const handleCategoryMouseLeave = () => {
	if (categoryAnchorRef.value?.contains(document.activeElement)) return;
	closeCategoryMenu();
};

const handleCategoryFocusOut = (event) => {
	if (categoryAnchorRef.value?.contains(event.relatedTarget)) return;
	closeCategoryMenu();
};

const focusCategoryMenuItem = async (boundary = 'first') => {
	openCategoryMenu();
	await nextTick();
	const items = getCategoryMenuItems();
	if (!items.length) return;
	items[boundary === 'last' ? items.length - 1 : 0]?.focus?.();
};

const getCategoryMenuItems = () =>
	Array.from(
		categoryMenuRef.value?.querySelectorAll('.category-submenu-option:not(:disabled)') || [],
	);

const focusAdjacentCategoryMenuItem = (direction) => {
	const items = getCategoryMenuItems();
	if (!items.length) return;
	const currentIndex = items.indexOf(document.activeElement);
	const nextIndex =
		currentIndex < 0
			? direction > 0
				? 0
				: items.length - 1
			: (currentIndex + direction + items.length) % items.length;
	items[nextIndex]?.focus?.();
};

const closeCategoryMenuAndFocusTrigger = () => {
	suppressCategoryFocusOpen.value = true;
	closeCategoryMenu();
	nextTick(() => {
		categoryAnchorRef.value?.querySelector('[aria-haspopup="menu"]')?.focus?.();
		suppressCategoryFocusOpen.value = false;
	});
};

const applyCompletionMode = (value) => {
	formModel.completionMode = value;
	formModel.requiresEffectiveDate = value === 'bilateral_signature';
	markChanged();
	closeCompletionPopover();
};

const selectCompletionMode = (value) => {
	if (value === formModel.completionMode) {
		closeCompletionPopover();
		return;
	}
	const signingHistoryCount = Number(props.document?.signingSummary?.totalExecutions ?? 0) || 0;
	if (signingHistoryCount > 0) {
		emit('completion-mode-change-request', {
			document: props.document,
			currentMode: formModel.completionMode,
			nextMode: value,
		});
		closeCompletionPopover();
		return;
	}
	applyCompletionMode(value);
};

const getEnabledMenuItems = () =>
	Array.from(
		menuRef.value?.querySelectorAll('[role="menuitem"]:not(:disabled)') || [],
	);

const focusBoundaryMenuItem = (boundary) => {
	const items = getEnabledMenuItems();
	if (!items.length) return;
	items[boundary === 'last' ? items.length - 1 : 0]?.focus?.();
};

const focusAdjacentMenuItem = (direction) => {
	const items = getEnabledMenuItems();
	if (!items.length) return;
	const currentIndex = items.indexOf(document.activeElement);
	const nextIndex =
		currentIndex < 0
			? direction > 0
				? 0
				: items.length - 1
			: (currentIndex + direction + items.length) % items.length;
	items[nextIndex]?.focus?.();
};

const captureMenuAnchor = (event) => {
	const hasPointerCoordinates =
		event &&
		event.type !== 'keydown' &&
		Number.isFinite(Number(event.clientX)) &&
		Number.isFinite(Number(event.clientY));
	if (hasPointerCoordinates) {
		menuAnchor.left = Number(event.clientX);
		menuAnchor.top = Number(event.clientY);
		return;
	}

	const rowRect = rowRef.value?.getBoundingClientRect?.();
	menuAnchor.left = Number(rowRect?.left || 0) + 12;
	menuAnchor.top = Number(rowRect?.bottom || 0) + 4;
};

const calculateMenuPosition = () => {
	const menuRect = menuRef.value?.getBoundingClientRect?.();
	const menuButtonCount = menuRef.value?.querySelectorAll?.('.submenu-btn').length || 5;
	const menuWidth = menuRect?.width || menuRef.value?.offsetWidth || 180;
	const menuHeight =
		menuRect?.height ||
		menuRef.value?.offsetHeight ||
		menuRef.value?.scrollHeight ||
		menuButtonCount * 40 + 12;
	const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : menuAnchor.left + menuWidth;
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : menuAnchor.top + menuHeight;
	const padding = 12;

	let left = menuAnchor.left;
	let top = menuAnchor.top;
	if (left + menuWidth > viewportWidth - padding) {
		left = viewportWidth - menuWidth - padding;
	}
	if (top + menuHeight > viewportHeight - padding) {
		top = viewportHeight - menuHeight - padding;
	}

	menuPosition.left = Math.max(padding, left);
	menuPosition.top = Math.max(padding, top);
};

const toggleMenu = (focusFirstItem = false, event = null) => {
	if (isBusy.value) return;
	menuOpen.value = !menuOpen.value;
	if (menuOpen.value) {
		captureMenuAnchor(event);
		closeCompletionPopover();
		closeTranslationsPopover();
		categoryMenuOpen.value = false;
		emit('menu-open', { id: props.document?.id });
		nextTick(() => {
			calculateMenuPosition();
			if (focusFirstItem) focusBoundaryMenuItem('first');
		});
	}
};

const closeMenu = () => {
	menuOpen.value = false;
	categoryMenuOpen.value = false;
};

const handleMenuEscape = () => {
	closeMenu();
	nextTick(() => rowRef.value?.focus?.());
};

const startRename = () => {
	if (isBusy.value || hasActiveSigning.value) return;
	renameOriginalTitle.value = formModel.title;
	renameStartVersion.value = formVersion.value;
	closeMenu();
	isRenaming.value = true;
	nextTick(() => {
		titleInputRef.value?.focus?.();
		titleInputRef.value?.select?.();
	});
};

const cancelRename = () => {
	clearAutosaveTimer();
	formModel.title = renameOriginalTitle.value;
	formVersion.value = renameStartVersion.value;
	autosaveState.value = 'idle';
	isRenaming.value = false;
	nextTick(() => rowRef.value?.focus?.());
};

const finishRename = () => {
	if (!isRenaming.value) return;
	const nextTitle = String(formModel.title || '').trim();
	if (!nextTitle) {
		cancelRename();
		return;
	}
	formModel.title = nextTitle;
	isRenaming.value = false;
	flushIfDirty();
};

const handleOpenDocument = () => {
	closeMenu();
	emit('open-document', { document: props.document });
};

const handleMoveCategory = (category) => {
	closeMenu();
	if (category === currentCategory.value) return;
	emit('move-category', {
		document: props.document,
		category,
	});
};

const handleArchive = () => {
	closeMenu();
	emit('archive', { document: props.document });
};

const handleRowClick = (event) => {
	toggleMenu(false, event);
};

const closePopovers = () => {
	closeCompletionPopover();
	closeTranslationsPopover();
	closeMenu();
};

const handleOutsideClick = (event) => {
	if (completionPopoverOpen.value) {
		const root = completionPopRef.value;
		if (root && !root.contains(event.target)) {
			closeCompletionPopover();
		}
	}
	if (translationsPopoverOpen.value) {
		const root = rowRef.value;
		if (root && !root.contains(event.target)) {
			closeTranslationsPopover();
		}
	}
	if (menuOpen.value) {
		const root = rowRef.value;
		if (root && !root.contains(event.target)) {
			closeMenu();
		}
	}
};

onMounted(() => {
	document.addEventListener('click', handleOutsideClick);
	document.addEventListener('pointerdown', handleOutsideClick);
	if (props.autoFocus) {
		nextTick(() => startRename());
	}
});

onBeforeUnmount(() => {
	clearAutosaveTimer();
	unlockTranslationModalEnvironment();
	document.removeEventListener('click', handleOutsideClick);
	document.removeEventListener('pointerdown', handleOutsideClick);
});

defineExpose({
	setSaveHandler,
	flushIfDirty,
	applyCompletionMode,
	closePopovers,
	focusTitle: () => titleInputRef.value?.focus?.(),
});
</script>

<style scoped>
.table-row {
	touch-action: manipulation;
	cursor: pointer;
	transition: background-color 0.15s, box-shadow 0.15s;
}

@media (hover: hover) {
	.table-row:hover { background: var(--noc-blue-05); }
}

.table-row.active {
	background: var(--noc-blue-05);
}

.table-row:focus-visible {
	outline: none;
	box-shadow: inset 0 0 0 2px var(--noc-blue, #0b5ae0);
}

.cell-input {
	border: 1px solid transparent;
	border-radius: 4px;
	padding: 4px 6px;
	background: transparent;
	color: var(--text-color);
	font-family: inherit;
	font-size: 0.9rem;
	width: 100%;
	box-sizing: border-box;
	outline: none;
	transition: background 0.15s ease, border-color 0.15s ease;
}

.cell-input:hover {
	background: var(--noc-background-secondary);
	border-color: var(--border-color);
}

.cell-input:focus {
	background: var(--noc-background);
	border-color: var(--noc-blue);
}

.title-input {
	font-weight: 600;
}

.title-cell-content {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.title-cell-content .title-input,
.title-cell-content .title-text {
	flex: 1 1 auto;
	min-width: 0;
}

.title-text {
	display: block;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 600;
}

.mobile-cell-label {
	display: none;
}

.assignment-cell-value {
	display: block;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	text-align: inherit;
}

.col-amount {
	text-align: right;
}

.col-mode {
	white-space: nowrap;
}

.badge-cell {
	position: relative;
	display: inline-flex;
}

.status-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 4px 10px;
	border-radius: 4px;
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1.15;
	text-transform: uppercase;
	border: 1px solid transparent;
	cursor: pointer;
	transition: all 0.2s ease;
	background-color: var(--noc-blue-10);
	color: var(--noc-blue);
	font-family: inherit;
	white-space: nowrap;
	letter-spacing: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	touch-action: manipulation;
}

.status-badge:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.status-bilateral {
	background: var(--noc-blue-10, #eaf2ff);
	color: var(--noc-blue, #0b5ae0);
}

.status-readonly {
	background: var(--noc-blue-10, #eaf2ff);
	color: var(--noc-blue, #0b5ae0);
}

.status-default {
	background: var(--noc-blue-10, #eaf2ff);
	color: var(--noc-blue, #0b5ae0);
}

.status-suppliers {
	background: var(--noc-blue-10, #eaf2ff);
	color: var(--noc-blue, #0b5ae0);
}

.status-empty {
	background: var(--noc-blue-10, #eaf2ff);
	color: var(--noc-blue, #0b5ae0);
}

.status-popover {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	padding: 14px;
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
	min-width: 280px;
	max-width: min(420px, calc(100vw - 32px));
	z-index: 9999;
	display: flex;
	flex-direction: column;
	gap: 10px;
	box-sizing: border-box;
	overflow: hidden;
}

.status-popover--completion {
	width: min(420px, calc(100vw - 32px));
	min-width: min(320px, calc(100vw - 32px));
}

.popover-title {
	margin: 0;
	font-size: 0.78rem;
	font-weight: 600;
	color: var(--text-color-secondary);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.popover-options {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.popover-option {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	align-items: center;
	gap: 10px;
	border: 1px solid transparent;
	background: transparent;
	border-radius: 6px;
	padding: 8px 10px;
	text-align: left;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease;
	font-family: inherit;
	width: 100%;
	box-sizing: border-box;
}

.popover-option:hover {
	background: var(--noc-background-secondary);
}

.popover-option.selected {
	background: var(--noc-blue-05);
	border-color: var(--noc-blue-20);
}

.popover-option-help {
	font-size: 0.78rem;
	color: var(--text-color-secondary);
	line-height: 1.35;
	min-width: 0;
	white-space: normal;
	overflow-wrap: anywhere;
}

.popover-option .status-badge {
	flex-shrink: 0;
	cursor: pointer;
	max-width: 100%;
}

.translation-modal-overlay {
	position: fixed;
	inset: 0;
	z-index: 1200;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(15, 23, 42, 0.45);
	box-sizing: border-box;
}

.translation-modal {
	display: flex;
	flex-direction: column;
	width: min(560px, calc(100vw - 32px));
	max-height: min(680px, calc(100vh - 32px));
	padding: 22px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: 0 18px 50px rgba(15, 23, 42, 0.24);
	box-sizing: border-box;
	overflow: hidden;
	outline: none;
}

.translation-modal-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	margin-bottom: 18px;
}

.translation-modal-title {
	margin: 0;
	color: var(--text-color);
	font-size: 1.2rem;
	font-weight: 600;
}

.translation-modal-subtitle {
	margin: 4px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.85rem;
}

.translation-modal-close {
	flex: 0 0 auto;
	padding: 7px 12px;
	background: transparent;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 4px;
	color: var(--text-color);
	font: inherit;
	font-size: 0.82rem;
	font-weight: 500;
	cursor: pointer;
}

.translation-modal-close:hover,
.translation-modal-close:focus-visible {
	background: var(--noc-background-secondary, #f5f5f5);
	outline: none;
}

.translation-list {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-height: 0;
	overflow-y: auto;
}

.translation-item {
	display: grid;
	grid-template-columns: 32px 80px 1fr;
	align-items: center;
	gap: 10px;
	padding: 6px 8px;
	border-radius: 6px;
	min-width: 0;
}

.translation-item:hover {
	background: var(--noc-background-secondary);
}

.translation-lang {
	font-size: 0.78rem;
	font-weight: 700;
	color: var(--text-color);
}

.translation-status {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 68px;
	padding: 4px 10px;
	border-radius: 4px;
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1.15;
	text-transform: uppercase;
	border: 1px solid transparent;
	color: var(--noc-blue, #0b5ae0);
	background: var(--noc-blue-10, #eaf2ff);
}

.translation-ready {
	background: var(--noc-green-20, #d1fae5);
	color: var(--noc-green-dark, #047857);
	border-color: transparent;
}

.translation-generating,
.translation-stale {
	background: #fef3c7;
	color: #9a6610;
	border-color: transparent;
}

.translation-failed {
	background: #fee2e2;
	color: #b91c1c;
	border-color: transparent;
}

.translation-actions {
	display: inline-flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 4px;
}

.translation-action {
	font-size: 0.78rem;
	padding: 2px 6px;
}

.link-action {
	background: none;
	border: none;
	color: var(--noc-blue);
	cursor: pointer;
	font-size: 0.82rem;
	font-weight: 600;
	padding: 2px 4px;
	border-radius: 4px;
	transition: background 0.15s ease;
}

.link-action:hover:not(:disabled) {
	background: var(--noc-blue-05);
}

.link-action:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.autosave-hint {
	margin-left: auto;
	flex: 0 0 auto;
	font-size: 0.75rem;
	color: var(--text-color-secondary);
}

.autosave-saving {
	color: var(--noc-blue);
}

.autosave-error {
	color: var(--noc-red);
}

.autosave-dirty {
	color: var(--text-color-secondary);
}

.row-submenu {
	position: fixed;
	background-color: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 4px;
	z-index: 1000;
	width: max-content;
	max-width: calc(100vw - 24px);
	padding: 6px 0;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 10px 16px;
	background: none;
	border: none;
	color: var(--text-color);
	text-align: left;
	font: 500 0.875rem/1.25rem var(--font-family-base);
	cursor: pointer;
	touch-action: manipulation;
	white-space: nowrap;
}

.submenu-btn:hover:not(:disabled),
.submenu-btn:focus-visible {
	background-color: var(--noc-blue-10);
	color: var(--noc-blue);
	outline: none;
}

.submenu-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.submenu-btn--with-meta,
.category-submenu-option {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.submenu-meta {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.75rem;
	font-weight: 500;
}

.category-flyout-anchor {
	position: relative;
}

.category-submenu {
	position: fixed;
	z-index: 1001;
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 180px;
	padding: 6px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 4px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06);
	animation: category-flyout-in 0.14s ease-out;
}

.category-submenu::before {
	content: '';
	position: absolute;
	top: 0;
	left: -7px;
	width: 7px;
	height: 100%;
}

.category-submenu--left::before {
	right: -7px;
	left: auto;
}

.category-submenu--left {
	animation-name: category-flyout-in-left;
}

.category-submenu-option {
	width: 100%;
	padding: 8px 12px;
	background: transparent;
	border: none;
	border-radius: 4px;
	color: var(--text-color-secondary, #6b7280);
	font-family: inherit;
	font-size: 0.84rem;
	font-weight: 500;
	text-align: left;
	white-space: nowrap;
	cursor: pointer;
}

.category-submenu-option:hover,
.category-submenu-option:focus-visible {
	background: var(--noc-background-secondary, #f5f5f5);
	color: var(--text-color);
	outline: none;
}

.category-submenu-option[aria-checked='true'] {
	background: var(--noc-blue-05, rgba(0, 101, 255, 0.06));
	color: var(--noc-blue, #0065ff);
}

@keyframes category-flyout-in {
	from {
		opacity: 0;
		transform: translateX(-4px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes category-flyout-in-left {
	from {
		opacity: 0;
		transform: translateX(4px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

.submenu-divider {
	height: 1px;
	margin: 4px 8px;
	background: var(--border-color, #e5e7eb);
}

.submenu-btn.delete {
	color: var(--noc-red);
}

.submenu-btn.delete:hover:not(:disabled),
.submenu-btn.delete:focus-visible {
	background-color: var(--noc-red-10);
	color: var(--noc-red);
}

.status-popover-enter-active,
.status-popover-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.status-popover-enter-from,
.status-popover-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}

@media (max-width: 560px) {
	.translation-modal {
		padding: 18px;
	}

	.translation-item {
		grid-template-columns: 32px minmax(0, 1fr);
	}

	.translation-actions {
		grid-column: 1 / -1;
		justify-content: flex-end;
	}
}

@container documents-table (max-width: 920px) {
	.col-reference,
	.col-counterparty,
	.col-site-address,
	.col-amount {
		flex-direction: column;
		align-items: flex-start;
		gap: 3px;
		text-align: left;
	}

	.mobile-cell-label {
		display: block;
		color: var(--text-color-secondary, #6b7280);
		font-size: 0.72rem;
		font-weight: 600;
		line-height: 1.2;
	}
}
</style>
